import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { MapView, reactiveUtils } from '../constructors';
import type { MapSurface } from '../types';
import { registerMapView, unregisterMapView } from '../helpers/map-instance-registry';
import { monitorMapHealth, reportMapInitFailure } from '../helpers/map-health-monitor';
import { logMapInfo } from '../helpers/utils';
import {
    MapTileCacheUseSnapshot,
    describeMapTileCacheUseSince,
    notifyMapDrawn,
    snapshotMapTileCacheUse,
} from '../helpers/map-tile-cache';
import { useMapHealthInputs } from '../hooks/use-map-health-inputs';
import { getGpuIdentity, releaseOrphanedContexts } from '../../monitoring/webgl-context-registry';

/**
 * The single place in the app where an Esri `MapView` may be constructed, and the
 * owner of its teardown.
 *
 * WHY THIS EXISTS. A MapView holds a WebGL context and a GPU texture atlas of every
 * map icon. Dropping the React component that rendered it does not release either —
 * only `view.destroy()` does. `use-esri-base-map` never called it, and its teardown
 * was registered in the same effect that creates the view *and* sets the `mapView`
 * state that effect depends on: React therefore ran the cleanup immediately after
 * mount (before the async `view.when()` work had assigned anything for it to clean
 * up) and registered nothing for the real unmount. Production sessions show the
 * result — 150 MapViews created, 16 contexts still live, which is Chromium's cap, at
 * which point the browser evicts the oldest context and the map goes black.
 *
 * Two structural properties prevent a repeat:
 *   1. teardown lives in a *dedicated* effect keyed only on the view, so no
 *      dependency-array mistake in a calling hook can stop it running (the same shape
 *      `use-esri-embedded-base-map` arrived at, now the only shape available);
 *   2. an ESLint rule bans `new MapView` everywhere but this file, so a future map
 *      cannot opt out of it.
 *
 * Every view it creates also gets health monitoring (`monitorMapHealth`) once it is
 * ready, and logs exactly one lifecycle event: `map_ready`, or the init failure — so the
 * same lint rule guarantees no map goes unmonitored or uncounted. A view that goes on to
 * finish drawing also logs `map_drawn`.
 *
 * Callers contribute their own view-scoped cleanup with {@link OwnedMapView.addDisposer}
 * rather than returning a cleanup function, which is what keeps (1) true.
 */

/** Constructor properties for a MapView, derived from the class so it cannot drift. */
export type MapViewProperties = ConstructorParameters<typeof MapView>[0];

type UseOwnedMapViewOptions = {
    /** Which UI this map belongs to. Also stamped on the container as
     * `data-map-surface` by the calling hook, which is how leaked contexts get
     * attributed back to a specific map. */
    surface: MapSurface;
    /** The view's container. Health monitoring watches its canvases for context loss. */
    mapRef: MutableRefObject<HTMLDivElement | null>;
    /**
     * Hand the WebGL context back to the browser after `destroy()`.
     *
     * Passed in rather than read here: `esri/` may not import app state, and this is
     * behind `cad.map_release_webgl_context_on_teardown.enabled` so it can be rolled
     * out per department. Esri's `destroy()` is supposed to make this unnecessary;
     * the flag exists because production says otherwise.
     */
    releaseWebGLContextOnTeardown?: boolean;
    /** See `MapHealthMonitorOptions.countSuspendCycles`. */
    countSuspendCycles?: boolean;
};

export type OwnedMapView = {
    /** The live view, or undefined before the caller has created one. */
    mapView: MapView | undefined;
    /**
     * Construct a MapView and take ownership of its lifetime. Calling this while a
     * view already exists replaces it — the previous one is torn down.
     */
    createMapView: (properties: MapViewProperties) => MapView;
    /**
     * Register cleanup to run at this view's teardown, before it is destroyed. Safe
     * to call asynchronously (e.g. from inside `view.when()`); disposers are held
     * per-view, so a late registration from a superseded view is never run against
     * its replacement.
     */
    addDisposer: (view: MapView, dispose: () => void) => void;
};

/**
 * Logs `map_drawn` the first time the view has nothing left to load, which is what a user
 * actually waits for. `map_ready` fires at `view.when()`, before a single tile is drawn.
 * Carries what the tile cache did meanwhile, so a rebuild served from the cache can be told
 * apart from one that downloaded everything. Nothing is logged for a view torn down first.
 */
function logWhenFirstDrawn({
    view,
    surface,
    createdAtMs,
    cacheUseAtCreation,
    addDisposer,
}: {
    view: MapView;
    surface: MapSurface;
    createdAtMs: number;
    cacheUseAtCreation: MapTileCacheUseSnapshot;
    addDisposer: (view: MapView, dispose: () => void) => void;
}): void {
    const controller = new AbortController();
    addDisposer(view, () => controller.abort());
    const drawn = reactiveUtils.whenOnce(() => !view.updating, controller.signal);
    drawn.then(
        () => {
            if (controller.signal.aborted) {
                return;
            }
            try {
                logMapInfo({
                    source: 'lifecycle',
                    event: 'map_drawn',
                    surface,
                    extraData: {
                        drawnMs: Date.now() - createdAtMs,
                        ...describeMapTileCacheUseSince(cacheUseAtCreation),
                    },
                });
                notifyMapDrawn();
            } catch {
                // Logging must never fail the map.
            }
        },
        () => {
            // Torn down before it finished drawing: nothing to report.
        }
    );
}

/**
 * Teardown, in an order that matters:
 *
 *   1. stop counting the view as live, so telemetry is right even if a later step fails;
 *   2. run the caller's disposers — this removes the `webglcontextlost` listeners
 *      *before* step 4 can trigger one, so a deliberate release is never logged as a
 *      real black-map failure;
 *   3. destroy the view, which is the step that was missing;
 *   4. optionally release the GPU context.
 *
 * Every step is independently guarded. A throw partway through would abandon all the
 * remaining steps — most importantly the `destroy()` this whole change exists to
 * guarantee — so no single hostile SDK state can strand the rest of the teardown.
 */
function teardownMapView({
    view,
    surface,
    disposers,
    releaseWebGLContext,
}: {
    view: MapView;
    surface: MapSurface;
    disposers: Array<() => void>;
    releaseWebGLContext: boolean;
}): void {
    try {
        unregisterMapView(surface);
    } catch {
        // Bookkeeping only.
    }

    for (const dispose of disposers) {
        try {
            dispose();
        } catch {
            // One disposer failing must not strand the others, or the destroy below.
        }
    }

    try {
        if (!view.destroyed) {
            view.destroy();
        }
    } catch {
        // Nothing useful to do; the release below is still worth attempting.
    }

    if (!releaseWebGLContext) {
        return;
    }
    // Deferred by a tick on purpose. `releaseOrphanedContexts` only touches contexts
    // whose canvas has left the document — that is what makes it safe when two maps
    // share a surface — and the canvases of the view we just destroyed are not
    // guaranteed to be detached synchronously (Esri may drop them itself, or React may
    // remove the container that holds them). One macrotask later, either has happened.
    try {
        setTimeout(() => releaseOrphanedContexts(surface), 0);
    } catch {
        // Releasing early is an optimisation, not a correctness requirement.
    }
}

export function useOwnedMapView({
    surface,
    mapRef,
    releaseWebGLContextOnTeardown = false,
    countSuspendCycles = false,
}: UseOwnedMapViewOptions): OwnedMapView {
    const [mapView, setMapView] = useState<MapView>();
    const disposersRef = useRef(new WeakMap<MapView, Array<() => void>>());
    const getMapHealthInputs = useMapHealthInputs();

    // Read through refs, never as dependencies: listing them would destroy and recreate a
    // working map the moment a feature flag resolved or a prop changed identity.
    const surfaceRef = useRef(surface);
    surfaceRef.current = surface;
    const releaseRef = useRef(releaseWebGLContextOnTeardown);
    releaseRef.current = releaseWebGLContextOnTeardown;
    const monitoringRef = useRef({ mapRef, countSuspendCycles });
    monitoringRef.current = { mapRef, countSuspendCycles };

    const addDisposer = useCallback(function addDisposer(view: MapView, dispose: () => void) {
        const disposers = disposersRef.current.get(view);
        if (disposers) {
            disposers.push(dispose);
        } else {
            // The view is already gone, so its teardown has run. Dispose now rather
            // than dropping the cleanup on the floor.
            try {
                dispose();
            } catch {
                // Best effort.
            }
        }
    }, []);

    const monitorWhenReady = useCallback(
        function monitorWhenReady(
            view: MapView,
            createdAtMs: number,
            cacheUseAtCreation: MapTileCacheUseSnapshot
        ) {
            const viewSurface = surfaceRef.current;
            view.when(
                () => {
                    try {
                        // One per MapView: the denominator every black-map rate needs,
                        // since nothing else fires for a map that works. Also records how
                        // long the view took to become ready (not to draw every layer),
                        // and the GPU it runs on.
                        logMapInfo({
                            source: 'lifecycle',
                            event: 'map_ready',
                            surface: viewSurface,
                            extraData: { readyMs: Date.now() - createdAtMs, ...getGpuIdentity() },
                        });
                    } catch {
                        // Logging must never fail the map.
                    }
                    try {
                        addDisposer(
                            view,
                            monitorMapHealth({
                                view,
                                surface: viewSurface,
                                createdAtMs,
                                ...monitoringRef.current,
                                ...getMapHealthInputs(),
                            })
                        );
                    } catch {
                        // Monitoring must never fail the map.
                    }
                    try {
                        logWhenFirstDrawn({
                            view,
                            surface: viewSurface,
                            createdAtMs,
                            cacheUseAtCreation,
                            addDisposer,
                        });
                    } catch {
                        // Logging must never fail the map.
                    }
                },
                (error: unknown) => {
                    try {
                        reportMapInitFailure(error, viewSurface, createdAtMs);
                    } catch {
                        // Logging must never fail the map.
                    }
                }
            );
        },
        [addDisposer, getMapHealthInputs]
    );

    const createMapView = useCallback(
        function createMapView(properties: MapViewProperties) {
            const createdAtMs = Date.now();
            // Taken before construction: the basemap starts loading as soon as the view exists.
            const cacheUseAtCreation = snapshotMapTileCacheUse();
            const view = new MapView(properties);
            disposersRef.current.set(view, []);
            registerMapView(surfaceRef.current);
            setMapView(view);
            monitorWhenReady(view, createdAtMs, cacheUseAtCreation);
            return view;
        },
        [monitorWhenReady]
    );

    useEffect(
        function destroyMapViewOnTeardown() {
            if (!mapView) {
                return;
            }

            // Captured here rather than read in the cleanup: the WeakMap instance is
            // created once and never reassigned, so this is the same object either
            // way, and capturing keeps `react-hooks/exhaustive-deps` satisfied without
            // a suppression.
            const disposersByView = disposersRef.current;

            return () => {
                const disposers = disposersByView.get(mapView) ?? [];
                disposersByView.delete(mapView);
                teardownMapView({
                    view: mapView,
                    surface: surfaceRef.current,
                    disposers,
                    releaseWebGLContext: releaseRef.current,
                });
            };
        },
        [mapView]
    );

    return { mapView, createMapView, addDisposer };
}
